import { getTrackerQuestionsApi } from "src/DAL/trackerQuestion/trackerQuestion";

export const QUESTION_MODES = {
  GENERAL: "general",
  MEASURABLE: "measurable",
  COACH_REVIEW: "coach_review",
};

export const MEASURE_DIRECTIONS = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
};

/** How a measurable question contributes to a shared series (hard link). */
export const MEASURE_ROLES = {
  NONE: "",
  BASELINE: "baseline",
  PROGRESS: "progress",
  TARGET: "target",
};

export const MEASURE_ROLE_OPTIONS = [
  { value: MEASURE_ROLES.NONE, label: "Standalone (own series)" },
  {
    value: MEASURE_ROLES.BASELINE,
    label: "Baseline (e.g. Starting Weight)",
  },
  {
    value: MEASURE_ROLES.PROGRESS,
    label: "Progress (e.g. Current Weight)",
  },
  { value: MEASURE_ROLES.TARGET, label: "Target / goal" },
];

export const getMeasureRoleLabel = (value) =>
  MEASURE_ROLE_OPTIONS.find((item) => item.value === value)?.label ||
  value ||
  "—";

/** Normalize series key: "Starting Weight" → "starting_weight", keep "weight". */
export const normalizeMeasureSeriesKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

export const MEASURE_TYPES = [
  {
    value: "weight",
    label: "Weight",
    defaultDirection: MEASURE_DIRECTIONS.ASCENDING,
    units: ["kg", "lbs", "st"],
  },
  {
    value: "time",
    label: "Time",
    defaultDirection: MEASURE_DIRECTIONS.ASCENDING,
    units: ["sec", "min", "hh:mm:ss"],
  },
  {
    value: "distance",
    label: "Distance",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["m", "km", "miles", "yards"],
  },
  {
    value: "height",
    label: "Height",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["cm", "m", "ft", "in"],
  },
  {
    value: "percentage",
    label: "Percentage",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["%"],
  },
  {
    value: "reps",
    label: "Reps / Count",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["reps", "count"],
  },
  {
    value: "score",
    label: "Score / Points",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: ["pts", "score"],
  },
  {
    value: "custom",
    label: "Custom",
    defaultDirection: MEASURE_DIRECTIONS.DESCENDING,
    units: [],
  },
];

export const QUESTION_FIELD_TYPES = [
  { value: "short_text", label: "Short Text" },
  { value: "long_text", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "scale", label: "Scale (1–10)" },
  { value: "email", label: "Email Address" },
  { value: "phone", label: "Phone Number" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "checkbox", label: "Checkbox" },
  { value: "yes_no", label: "Yes / No" },
  { value: "status", label: "Status" },
  { value: "coach", label: "Coach" },
  { value: "gym", label: "Gym" },
  { value: "programme", label: "Programme" },
  { value: "review_date", label: "Review Date" },
  { value: "follow_up_date", label: "Follow-up Date" },
  { value: "member", label: "Member" },
  { value: "image_upload", label: "Image Upload" },
  { value: "file_upload", label: "File Upload" },
];

export const getFieldTypeLabel = (value) =>
  QUESTION_FIELD_TYPES.find((item) => item.value === value)?.label || value || "—";

export const getQuestionModeLabel = (mode) => {
  if (mode === QUESTION_MODES.MEASURABLE) return "Measurable";
  if (mode === QUESTION_MODES.COACH_REVIEW) return "Coach review";
  return "General";
};

export const getMeasureTypeLabel = (value) =>
  MEASURE_TYPES.find((item) => item.value === value)?.label || value || "—";

export const getMeasureTypeMeta = (value) =>
  MEASURE_TYPES.find((item) => item.value === value) || null;

export const isScaleFieldType = (fieldType) => fieldType === "scale";

/** Build scale options e.g. 1..10 */
export const getScaleOptions = (min = 1, max = 10) => {
  const start = Math.max(0, Number(min) || 1);
  const end = Math.max(start, Number(max) || 10);
  const list = [];
  for (let n = start; n <= end; n += 1) list.push(String(n));
  return list;
};

const pickText = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
};

/**
 * When a tracker uses a library question, fill empty placeholder / help / type
 * from the library record so hints show on the form.
 */
export const enrichQuestionFromLibrary = (question, libraryItem) => {
  if (!question) return question;
  if (!libraryItem) return { ...question };

  const isScale =
    isScaleFieldType(question.fieldType) ||
    isScaleFieldType(libraryItem.fieldType);
  const scaleMin = question.scaleMin ?? libraryItem.scaleMin ?? 1;
  const scaleMax = question.scaleMax ?? libraryItem.scaleMax ?? 10;

  return {
    ...question,
    label: pickText(question.label, libraryItem.label),
    placeholder: pickText(question.placeholder, libraryItem.placeholder),
    helpText: pickText(question.helpText, libraryItem.helpText),
    fieldType: question.fieldType || libraryItem.fieldType || "short_text",
    mode: question.mode || libraryItem.mode || QUESTION_MODES.GENERAL,
    measureType: question.measureType || libraryItem.measureType || "",
    measureSeriesKey:
      question.measureSeriesKey || libraryItem.measureSeriesKey || "",
    measureRole: question.measureRole || libraryItem.measureRole || "",
    units:
      Array.isArray(question.units) && question.units.length
        ? question.units
        : libraryItem.units || [],
    defaultUnit: question.defaultUnit || libraryItem.defaultUnit || "",
    measureDirection:
      question.measureDirection ||
      libraryItem.measureDirection ||
      MEASURE_DIRECTIONS.DESCENDING,
    scaleMin,
    scaleMax,
    options:
      Array.isArray(question.options) && question.options.length
        ? question.options
        : isScale
          ? getScaleOptions(scaleMin, scaleMax)
          : Array.isArray(libraryItem.options)
            ? libraryItem.options
            : [],
    measurable:
      Boolean(question.measurable) ||
      question.mode === QUESTION_MODES.MEASURABLE ||
      libraryItem.mode === QUESTION_MODES.MEASURABLE,
  };
};

export const needsOptions = (fieldType) =>
  ["dropdown", "multiple_choice", "checkbox", "status"].includes(fieldType);

export const createEmptyQuestion = () => ({
  id: `question_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  label: "",
  placeholder: "",
  helpText: "",
  fieldType: "short_text",
  mode: QUESTION_MODES.GENERAL,
  measureType: "",
  measureSeriesKey: "",
  measureRole: MEASURE_ROLES.NONE,
  units: [],
  defaultUnit: "",
  customUnits: [],
  measureDirection: MEASURE_DIRECTIONS.DESCENDING,
  scaleMin: 1,
  scaleMax: 10,
  options: [],
  status: "active",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const normalizeQuestion = (q) => {
  if (!q) return q;
  const id = q._id || q.id;
  return {
    ...q,
    id,
    _id: id,
  };
};

/**
 * In-memory copy of the API question library (`/api/tracker_question`) for
 * this session — refreshed by every page that loads the library, never persisted.
 */
let questionLibrary = [];

export const setQuestionLibrary = (questions = []) => {
  questionLibrary = Array.isArray(questions)
    ? questions.map(normalizeQuestion).filter(Boolean)
    : [];
  return questionLibrary;
};

export const getQuestionLibrary = () => questionLibrary;

/** Look up a library question by API id or starter `libraryKey` (e.g. q_email). */
export const getQuestionById = (id) => {
  if (!id) return null;
  const needle = String(id);
  return (
    questionLibrary.find(
      (question) =>
        String(question.id) === needle ||
        String(question._id) === needle ||
        (question.libraryKey && question.libraryKey === needle)
    ) || null
  );
};

/** Load the question library from the API (no local fallback). */
export const loadQuestionLibraryFromApi = async () => {
  try {
    const res = await getTrackerQuestionsApi({ limit: 500 });
    if (res?.code === 200 && Array.isArray(res.questions)) {
      return { ok: true, questions: setQuestionLibrary(res.questions) };
    }
    return {
      ok: false,
      questions: [],
      message: res?.message || "Failed to load question library",
    };
  } catch (_e) {
    return {
      ok: false,
      questions: [],
      message: "Failed to load question library",
    };
  }
};
